import { useEffect, useRef, useState } from 'react';

import type { SearchPageCriteria, SearchPageFacetStates } from '../sharedTypes';
import {
  canSearchListLoadMore,
  createSearchListExecutionState,
  resolveSearchListLoadMoreCursor,
  resolveSearchListLoadMoreError,
  resolveSearchListLoadMoreSuccess,
  resolveSearchListReloadError,
  resolveSearchListReloadSuccess,
  resetSearchListExecutionForCriteriaChange,
  resetSearchListExecutionForSortChange,
  startSearchListLoadMore,
  startSearchListReload,
} from './executionState';
import type {
  MultiListExecutionStateMap,
  MultiListSearchDefinitions,
  MultiListUrlStateMap,
} from './types';
import { sanitizeSortValue } from './urlState';

type UseMultiSearchListStateOptions<
  FilterName extends string,
  Lists extends MultiListSearchDefinitions<FilterName>,
> = {
  enabled: boolean;
  criteria: SearchPageCriteria<FilterName>;
  lists: Lists;
  listKeys: Array<keyof Lists & string>;
};

type UseMultiSearchListStateResult<
  FilterName extends string,
  Lists extends MultiListSearchDefinitions<FilterName>,
> = {
  listsState: MultiListExecutionStateMap<FilterName, Lists>;
  applyUrlState: (
    criteria: SearchPageCriteria<FilterName>,
    urlState: MultiListUrlStateMap<FilterName, Lists>
  ) => void;
  reloadAfterCriteriaChange: (criteria: SearchPageCriteria<FilterName>) => void;
  resetAllAndReload: (criteria: SearchPageCriteria<FilterName>) => void;
  loadMore: (listKey: keyof Lists & string) => void;
  setListSort: (listKey: keyof Lists & string, nextSort: string | null) => void;
};

const createDefaultMultiListExecutionState = <
  FilterName extends string,
  Lists extends MultiListSearchDefinitions<FilterName>,
>(
  definitions: Lists,
  listKeys: Array<keyof Lists & string>
): MultiListExecutionStateMap<FilterName, Lists> => {
  const state = {} as MultiListExecutionStateMap<FilterName, Lists>;

  listKeys.forEach((listKey) => {
    state[listKey] = createSearchListExecutionState(
      definitions[listKey],
      {} as SearchPageFacetStates<FilterName>
    ) as MultiListExecutionStateMap<FilterName, Lists>[typeof listKey];
  });

  return state;
};

export const useMultiSearchListState = <
  FilterName extends string,
  Lists extends MultiListSearchDefinitions<FilterName>,
>(
  options: UseMultiSearchListStateOptions<FilterName, Lists>
): UseMultiSearchListStateResult<FilterName, Lists> => {
  const definitionsRef = useRef(options.lists);
  const criteriaRef = useRef(options.criteria);
  const [listsState, setListsState] = useState<MultiListExecutionStateMap<FilterName, Lists>>(() =>
    createDefaultMultiListExecutionState(options.lists, options.listKeys)
  );
  const listsStateRef = useRef(listsState);
  const requestIdRef = useRef<Record<string, number>>({});
  const abortControllerRef = useRef<Record<string, AbortController | undefined>>({});
  const skipNextEnabledReloadRef = useRef(false);

  definitionsRef.current = options.lists;
  criteriaRef.current = options.criteria;
  listsStateRef.current = listsState;

  const getNextRequestId = (listKey: string) => {
    const nextRequestId = (requestIdRef.current[listKey] ?? 0) + 1;
    requestIdRef.current[listKey] = nextRequestId;
    return nextRequestId;
  };

  const abortListRequest = (listKey: string) => {
    abortControllerRef.current[listKey]?.abort();
  };

  const runReload = <ListKey extends keyof Lists & string>(
    listKey: ListKey,
    criteria: SearchPageCriteria<FilterName>,
    nextState: MultiListExecutionStateMap<FilterName, Lists>[ListKey]
  ) => {
    const definition = definitionsRef.current[listKey];
    const requestId = getNextRequestId(listKey);

    abortListRequest(listKey);

    const controller = new AbortController();
    abortControllerRef.current[listKey] = controller;

    setListsState((currentState) => ({
      ...currentState,
      [listKey]: startSearchListReload(nextState),
    }));

    void definition
      .fetcher({
        listKey,
        query: criteria.query,
        filters: criteria.filters,
        sort: nextState.sort,
        take: nextState.take,
        signal: controller.signal,
      })
      .then((result) => {
        if (controller.signal.aborted || requestIdRef.current[listKey] !== requestId) {
          return;
        }

        setListsState((currentState) => ({
          ...currentState,
          [listKey]: resolveSearchListReloadSuccess(currentState[listKey], definition, result),
        }));
      })
      .catch((error) => {
        if (controller.signal.aborted || requestIdRef.current[listKey] !== requestId) {
          return;
        }

        setListsState((currentState) => ({
          ...currentState,
          [listKey]: resolveSearchListReloadError(currentState[listKey], error),
        }));
      });
  };

  const runReloadAll = (
    criteria: SearchPageCriteria<FilterName>,
    nextState: MultiListExecutionStateMap<FilterName, Lists>
  ) => {
    options.listKeys.forEach((listKey) => {
      runReload(listKey, criteria, nextState[listKey]);
    });
  };

  useEffect(() => {
    if (!options.enabled) {
      return;
    }

    if (skipNextEnabledReloadRef.current) {
      skipNextEnabledReloadRef.current = false;
      return;
    }

    runReloadAll(criteriaRef.current, listsStateRef.current);
  }, [options.enabled]);

  useEffect(() => {
    return () => {
      Object.values(abortControllerRef.current).forEach((controller) => controller?.abort());
    };
  }, []);

  const applyUrlState = (
    criteria: SearchPageCriteria<FilterName>,
    urlState: MultiListUrlStateMap<FilterName, Lists>
  ) => {
    const nextState = options.listKeys.reduce<MultiListExecutionStateMap<FilterName, Lists>>(
      (state, listKey) => {
        const currentState = listsStateRef.current[listKey];

        state[listKey] = {
          ...currentState,
          take: urlState[listKey].take,
          sort: urlState[listKey].sort,
          hasLoaded: false,
          error: undefined,
          nextCursor: undefined,
        };

        return state;
      },
      {} as MultiListExecutionStateMap<FilterName, Lists>
    );

    setListsState(nextState);
    skipNextEnabledReloadRef.current = !options.enabled;
    runReloadAll(criteria, nextState);
  };

  const reloadAfterCriteriaChange = (criteria: SearchPageCriteria<FilterName>) => {
    const nextState = options.listKeys.reduce<MultiListExecutionStateMap<FilterName, Lists>>(
      (state, listKey) => {
        state[listKey] = resetSearchListExecutionForCriteriaChange(
          listsStateRef.current[listKey],
          definitionsRef.current[listKey]
        );
        return state;
      },
      {} as MultiListExecutionStateMap<FilterName, Lists>
    );

    setListsState(nextState);
    runReloadAll(criteria, nextState);
  };

  const resetAllAndReload = (criteria: SearchPageCriteria<FilterName>) => {
    const nextState = createDefaultMultiListExecutionState<FilterName, Lists>(
      options.lists,
      options.listKeys
    );

    setListsState(nextState);
    runReloadAll(criteria, nextState);
  };

  const loadMore = (listKey: keyof Lists & string) => {
    const currentState = listsStateRef.current[listKey];

    if (!canSearchListLoadMore(currentState)) {
      return;
    }

    const definition = definitionsRef.current[listKey];
    const requestId = getNextRequestId(listKey);
    const nextState = startSearchListLoadMore(currentState, definition);
    const cursor = resolveSearchListLoadMoreCursor(currentState, definition);

    abortListRequest(listKey);

    const controller = new AbortController();
    abortControllerRef.current[listKey] = controller;

    setListsState((currentStateMap) => ({
      ...currentStateMap,
      [listKey]: nextState,
    }));

    void definition
      .fetcher({
        listKey,
        query: criteriaRef.current.query,
        filters: criteriaRef.current.filters,
        sort: currentState.sort,
        take: definition.pageSize,
        cursor,
        signal: controller.signal,
      })
      .then((result) => {
        if (controller.signal.aborted || requestIdRef.current[listKey] !== requestId) {
          return;
        }

        setListsState((currentStateMap) => ({
          ...currentStateMap,
          [listKey]: resolveSearchListLoadMoreSuccess(currentStateMap[listKey], definition, result),
        }));
      })
      .catch((error) => {
        if (controller.signal.aborted || requestIdRef.current[listKey] !== requestId) {
          return;
        }

        setListsState((currentStateMap) => ({
          ...currentStateMap,
          [listKey]: resolveSearchListLoadMoreError(currentStateMap[listKey], definition, error),
        }));
      });
  };

  const setListSort = (listKey: keyof Lists & string, nextSort: string | null) => {
    const definition = definitionsRef.current[listKey];
    const sanitizedSort = sanitizeSortValue(
      nextSort ?? undefined,
      definition.sortOptions,
      definition.defaultSort ?? null
    );
    const nextState = resetSearchListExecutionForSortChange(
      listsStateRef.current[listKey],
      definition,
      sanitizedSort
    ) as MultiListExecutionStateMap<FilterName, Lists>[typeof listKey];

    setListsState((currentState) => ({
      ...currentState,
      [listKey]: nextState,
    }));
    runReload(listKey, criteriaRef.current, nextState);
  };

  return {
    listsState,
    applyUrlState,
    reloadAfterCriteriaChange,
    resetAllAndReload,
    loadMore,
    setListSort,
  };
};
